import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { clearActiveOrg, PolicyContextProvider, setActiveOrgId, useDiscoverOrganizations, } from "@piiqtechnologies/ui-lib";
import { PeekScreenLoader } from "@piiqtechnologies/ui-components";
import { PATHS } from "@constants/Paths";
import Restricted from "@pages/restricted";
import { ActiveOrganizationProvider } from "@stores/ActiveOrganizationContext";
import { useAuthContext } from "@stores/AuthContext";
import { getStoredMemberships, storeMemberships, toStoredMemberships } from "@stores/memberships";
import { generateAllowedActions, getIsAdmin } from "@stores/utils";
/**
 * Resolves the `/o/:orgId` segment against the memberships the signed-in user holds. Every check
 * is local — no request is ever sent against an organization the caller does not hold:
 *
 * - staff: redirected to the unprefixed admin dashboard (staff URLs stay unprefixed);
 * - memberships never fetched this session (legacy session): fetched once, stored, then resolved
 *   (screen loader meanwhile);
 * - `:orgId` held: the active-org mirror is written synchronously before any child renders;
 * - `:orgId` not held: the mirror is cleared and the restricted screen renders in place —
 *   a message, never a silent redirect.
 */
export const ActiveOrgLayout = () => {
    const { user } = useAuthContext();
    const params = useParams();
    const orgId = params.orgId;
    const isAdmin = getIsAdmin(user);
    const [memberships, setMemberships] = useState(() => getStoredMemberships());
    const [fetchOrganizations, organizations, , organizationsError] = useDiscoverOrganizations();
    const fetchStartedRef = useRef(false);
    // Legacy session (memberships never fetched this tab): fetch the caller's organizations once.
    useEffect(() => {
        if (isAdmin || memberships !== null || fetchStartedRef.current)
            return;
        fetchStartedRef.current = true;
        fetchOrganizations();
    }, [isAdmin, memberships, fetchOrganizations]);
    useEffect(() => {
        if (memberships !== null || !organizations)
            return;
        const fetched = toStoredMemberships(organizations);
        storeMemberships(fetched);
        setMemberships(fetched);
    }, [memberships, organizations]);
    // Resolved means we know the answer: memberships are held (even if empty), or the fetch failed
    // (fail closed — render Restricted rather than spin forever; nothing is persisted, so a reload
    // retries the fetch).
    const resolved = memberships !== null || Boolean(organizationsError);
    const membership = memberships === null || memberships === void 0 ? void 0 : memberships.find((entry) => entry._id === orgId);
    // The mirror write happens during render, NOT in an effect: effects run only after children
    // render, and a child request firing with a stale or absent `X-Peek-Organization` header is
    // exactly the bug this resolver exists to prevent. `undefined` means nothing has been applied
    // yet, so a non-member's first render still clears whatever a previous page load left behind.
    const lastWrittenRef = useRef(undefined);
    if (!isAdmin && membership && lastWrittenRef.current !== orgId) {
        setActiveOrgId(orgId); // synchronous, before <Outlet /> mounts
        lastWrittenRef.current = orgId;
    }
    else if (!isAdmin && resolved && !membership && lastWrittenRef.current !== null) {
        clearActiveOrg();
        lastWrittenRef.current = null;
    }
    // Computed before the early returns below so the hook order stays stable across renders; the
    // value is only consumed once a membership has resolved.
    const orgAllowedActions = useMemo(() => generateAllowedActions({ user, orgRole: membership === null || membership === void 0 ? void 0 : membership.role }), [user, membership === null || membership === void 0 ? void 0 : membership.role]);
    if (isAdmin)
        return React.createElement(Navigate, { to: PATHS.ADMIN_DASHBOARD, replace: true });
    if (!resolved)
        return React.createElement(PeekScreenLoader, { layered: true });
    if (!memberships || !membership)
        return React.createElement(Restricted, null);
    return (
    // The policy context is re-provided here, overriding the account-level one AuthContext mounts,
    // because capabilities belong to a membership rather than to an account: the same person can be
    // an admin in one organization and a viewer in another. This is the first point where the
    // organization is both known and authorized, and it sits above every org-scoped page, so each
    // of them reads the role for the organization it is actually rendering.
    React.createElement(PolicyContextProvider, { allow: orgAllowedActions, deny: [] },
        React.createElement(ActiveOrganizationProvider, { key: orgId, activeOrgId: orgId, activeOrg: membership, memberships: memberships },
            React.createElement(Outlet, null))));
};
